import { VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import { useStoreShare } from "hooks/shares";
import { useGetClientsOptions } from "hooks/users";
import { useState } from "react";
import moment from "moment";
import {
  Button,
  CalendarInput,
  DropFileInput,
  FullscreenModal,
  Input,
  SelectComponent,
  Warning,
  LargeSwitch,
} from "shared";
import { getCurrencyFromUuid } from "utils";
import * as Yup from "yup";
import "./AddShares.scss";
import { useGetMe } from "hooks/auth";

interface AddSharesModalProps {
  isOpen: boolean;
  hardClose: () => void;
  onClose: () => void;
  onSuccess?: (data?: any) => void;
  company?: any;
  setDetailsCompany?: (company: any) => void;
  detailsCompany?: any;
}

const AddSharesModal: React.FC<AddSharesModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  company,
  setDetailsCompany,
  detailsCompany,
  hardClose,
}) => {
  const { mutateAsync: storeShare } = useStoreShare();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: authorizedUser } = useGetMe();
  const isAdmin = authorizedUser?.role === "admin";
  const { data: clientOptions } = useGetClientsOptions(isOpen && isAdmin);
  const isBuy = company?.type === "buy" && !isAdmin;

  const initialValues = {
    userId: company?.editTransaction
      ? !isAdmin
        ? authorizedUser?.id
        : company?.share?.user?.id
      : null,
    companyId: company?.id ? company.id : "",
    sharesCount: company?.editTransaction ? company?.sharesCount : 0,
    sharesValue: company?.editTransaction ? company?.sharesValue : 0,
    invoice: company?.editTransaction ? company?.file || null : null,
    completedAt:
      company?.editTransaction && company?.completedAt
        ? moment(company?.completedAt).format("YYYY-MM-DD")
        : null,
    pricePerShare: company?.pricePerShare || null,
    currencyUuid: company?.editTransaction
      ? !isAdmin
        ? company?.currencyUuid
        : company?.share?.currencyUuid
      : company?.currencyUuid,
    transactionUuid: company?.editTransaction ? company?.uuid : null,
    isAdmin,
    type: company?.editTransaction
      ? company?.type === "buy"
        ? "buy"
        : "sell"
      : "sell",
  };

  const onSubmit = async (values: typeof initialValues | any) => {
    setIsSubmitting(true);

    try {
      const summaryRaw =
        values.sharesValue && values.sharesCount
          ? values.sharesValue / values.sharesCount
          : 0;

      const summary =
        Number.isFinite(summaryRaw) && !Number.isNaN(summaryRaw)
          ? Math.round(summaryRaw * 100) / 100
          : 0;

      await storeShare(
        { ...values, pricePerShare: summary },
        {
          onSuccess: (data) => {
            onSuccess?.(data);
            if (data.availableShares > 0 && setDetailsCompany) {
              setDetailsCompany({
                type: "",
                company: {
                  ...detailsCompany.company,
                  availableShares: data?.availableShares,
                  sharesCount: data?.availableShares,
                },
              });
              onClose();
            } else {
              hardClose();
            }
          },
        },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // KLUCZ: ta sama logika co w UI (editTransaction korzysta z company.isStock)
  const isStockCompany = company?.editTransaction
    ? !!company?.isStock
    : company?.type === "stock";

  const validation = () =>
    Yup.object().shape({
      userId: Yup.mixed().when(["isAdmin", "transactionUuid", "type"], {
        is: (
          isAdminField: boolean,
          transactionUuid: string | null,
          type: string,
        ) => isAdminField && !transactionUuid && type !== "buy",
        then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      }),
      sharesCount: Yup.number()
        .nullable()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return null;
          const casted =
            typeof originalValue === "string" ? Number(originalValue) : value;
          return Number.isNaN(casted) ? null : casted;
        })
        .required(VALIDATION_ENUM.REQUIRED)
        .when([], {
          is: () => isStockCompany,
          then: (schema) =>
            schema
              .min(1, VALIDATION_ENUM.MIN_1)
              .test(
                "count-integer-if-stock",
                "Ilość akcji musi być liczbą całkowitą",
                (value) => value == null || Number.isInteger(value),
              ),
          otherwise: (schema) =>
            schema
              .min(0.01, VALIDATION_ENUM.MIN_0_1_SHARES)
              .test(
                "max-2-decimals",
                "Ilość udziałów może mieć maksymalnie 2 miejsca po przecinku",
                (value) => value == null || Number(value.toFixed(2)) === value,
              ),
        }),
      sharesValue: Yup.number()
        .nullable()
        .optional()
        .transform((value, originalValue) => {
          if (originalValue === "" || originalValue == null) return null;
          const casted =
            typeof originalValue === "string" ? Number(originalValue) : value;
          return Number.isNaN(casted) ? null : casted;
        }),
      invoice: Yup.mixed().when(["isAdmin", "type"], {
        is: (isAdminField: boolean, typeField: string) => {
          const hasCompanyUser = !!company?.userId;
          const hasShareUser = !!company?.share?.user;
          return (
            (isAdminField && typeField === "sell") ||
            hasCompanyUser ||
            hasShareUser
          );
        },
        then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      }),
      completedAt: Yup.string().when(["isAdmin", "transactionUuid"], {
        is: (isAdminField: boolean, transactionUuid: string | null) =>
          !isAdminField || !transactionUuid,
        then: (schema) => schema.required(VALIDATION_ENUM.REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      }),
    });

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <h2 className="fullscreen-modal__header">
        {company?.editTransaction ? "Edycja" : "Dodawanie"} transakcji spółki{" "}
        <i>{company?.name}</i>
      </h2>
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
      >
        {({ errors, values, handleSubmit, setFieldValue, resetForm }) => {
          const totalAvailable = company?.editTransaction
            ? values.type === "sell"
              ? Number(company?.availableShares) + Number(company?.sharesCount)
              : Number(company?.availableShares) || 0
            : Number(company?.availableShares) || 0;
          const totalAvailableRounded = Math.round(totalAvailable * 100) / 100;
          const isMoreThanAvailable =
            Math.round(Number(values.sharesCount) * 100) / 100 >
            totalAvailableRounded;

          const summaryRaw =
            !!values?.sharesValue &&
            !!values?.sharesCount &&
            values?.sharesValue > 0 &&
            values?.sharesCount > 0
              ? values?.sharesValue / values?.sharesCount
              : 0;

          const summary =
            Number.isFinite(summaryRaw) && !Number.isNaN(summaryRaw)
              ? Math.round(summaryRaw * 100) / 100
              : 0;

          const isStock = company?.editTransaction
            ? company?.isStock
            : company?.type === "stock";

          const shouldShowClientField =
            isAdmin &&
            !(
              company?.share?.userId === null &&
              company?.type === "buy" &&
              company?.share?.origin === "reliance"
            );

          return (
            <Form className="m-width-100">
              {!company?.editTransaction && (
                <LargeSwitch
                  state={values.type}
                  className="add-company__switch"
                  options={[
                    { label: "Sprzedaż", value: "sell" },
                    { label: "Kupno", value: "buy" },
                  ]}
                  onChange={(value: string) => {
                    resetForm({
                      values: {
                        ...initialValues,
                        type: value,
                      },
                    });
                  }}
                />
              )}

              {values.type !== "buy" && !isBuy && shouldShowClientField && (
                <Field
                  label="Klient"
                  name="userId"
                  id="userId"
                  as={SelectComponent}
                  options={clientOptions}
                  isSearchable
                />
              )}

              <Field
                type="number"
                id="sharesCount"
                name="sharesCount"
                precision={isStock ? 0 : 2}
                inputMode={isStock ? "numeric" : "decimal"}
                step={isStock ? 1 : "any"}
                label={`Ilość ${isStock ? "akcji" : "udziałów"}`}
                as={Input}
              />
              {((values.type !== "buy" && !isBuy && shouldShowClientField) ||
                company?.share?.userId ||
                values.type === "sell") && (
                <Warning
                  className={isMoreThanAvailable ? "" : "warning--gray"}
                  text={`Nie możesz sprzedać więcej udziałów niż pozostało. Pozostało: ${
                    Number(totalAvailableRounded) % 1 === 0
                      ? String(Number(totalAvailableRounded))
                      : Number(totalAvailableRounded).toFixed(2)
                  }`}
                />
              )}

              <div className="input--currency">
                <Field
                  type="number"
                  id="sharesValue"
                  name="sharesValue"
                  label={`Wartość ${isStock ? "akcji" : "udziałów"}`}
                  maxLength={12}
                  precision={2}
                  optional={!!company?.editTransaction}
                  as={Input}
                />
                <span>
                  {company && getCurrencyFromUuid(company?.currencyUuid)}
                </span>
              </div>
              <div className={`add-share__summary`}>
                <label className="input__label">
                  Wartość {isStock ? "jednej akcji" : "jednego udziału"}
                </label>
                <input
                  type="text"
                  className="input"
                  readOnly
                  value={(() => {
                    const n = Number(summary || 0);
                    return n % 1 === 0 ? String(n) : n.toFixed(2);
                  })()}
                />
                <span>
                  {company && getCurrencyFromUuid(company?.currencyUuid)}
                </span>
              </div>

              <Field
                type="date"
                id="completedAt"
                name="completedAt"
                label={"Data transakcji"}
                component={CalendarInput}
                optional={isAdmin && !!initialValues.transactionUuid}
                errors={errors}
              />

              <Field
                label="Umowa"
                name="invoice"
                optional={
                  !company?.userId &&
                  !company?.share?.user &&
                  !(values.isAdmin && values.type === "sell")
                }
                id="invoice"
                as={DropFileInput}
                accept=".png, .jpg, .doc, .docx, .pdf"
                description="Maks. 5 MB (PDF, DOC, DOCX, JPG, JPEG, PNG)"
              />

              <div className="fullscreen-modal__buttons">
                <Button
                  label="Anuluj"
                  onClick={onClose}
                  className="button--stroke button--rounded"
                />
                <Button
                  label={company?.editTransaction ? "Potwierdź" : "Dodaj"}
                  type="submit"
                  disabled={isMoreThanAvailable && values.type === "sell"}
                  onClick={() => null}
                  className=" button--rounded button--black"
                  isLoading={isSubmitting}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </FullscreenModal>
  );
};

export default AddSharesModal;
